import React from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  Switch,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Alert,
  AlertIcon,
  Heading,
  Divider,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import { useInstitutionConfig } from "./InstitutionConfigContext";

export default function FormsComplianceTab() {
  const {
    formData,
    handleUpdateField,
  } = useInstitutionConfig();

  return (
    <VStack spacing={8} align="stretch">
      {/* Forms Library */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <HStack spacing={3}>
            <Heading size="md" color="white">Institutional Forms Library</Heading>
            <Badge colorScheme={formData.enableFormsLibrary ? "green" : "gray"}>
              {formData.enableFormsLibrary ? "Enabled" : "Disabled"}
            </Badge>
          </HStack>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Enable the institutional forms library for standardized clinical evaluation forms, field internship forms, and compliance documentation.
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0} flex="1">
                  <FormLabel color="white" mb={0}>Enable Forms Library</FormLabel>
                  <Text fontSize="sm" color="gray.400">
                    Master toggle for the institutional forms system. When enabled, instructors and staff can access, distribute, and collect standardized forms.
                  </Text>
                </VStack>
                <Switch
                  isChecked={formData.enableFormsLibrary || false}
                  onChange={(e) => handleUpdateField("enableFormsLibrary", e.target.checked)}
                  colorScheme="blue"
                  size="lg"
                />
              </HStack>
            </FormControl>

            {!formData.enableFormsLibrary && (
              <Alert status="info" fontSize="sm">
                <AlertIcon />
                <Text>Enable the Forms Library to access variance detection, PHI detection, encounter verification, and QR code settings below.</Text>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Compliance Features - only shown when Forms Library is enabled */}
      {formData.enableFormsLibrary && (
        <>
          {/* Variance Detection */}
          <Card bg="gray.800" borderColor="gray.600">
            <CardHeader>
              <Heading size="md" color="white">Variance Detection</Heading>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Monitor form submissions for IP address, location, and timing anomalies that may indicate compliance issues.
              </Text>
            </CardHeader>
            <CardBody>
              <FormControl>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0} flex="1">
                    <FormLabel color="white" mb={0}>Enable Variance Detection</FormLabel>
                    <Text fontSize="sm" color="gray.400">
                      When enabled, the system will flag submissions with unusual patterns such as rapid completion times, mismatched locations, or submissions from unexpected IP addresses.
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={formData.enableVarianceDetection || false}
                    onChange={(e) => handleUpdateField("enableVarianceDetection", e.target.checked)}
                    colorScheme="orange"
                  />
                </HStack>
              </FormControl>
            </CardBody>
          </Card>

          {/* PHI Detection */}
          <Card bg="gray.800" borderColor="gray.600">
            <CardHeader>
              <Heading size="md" color="white">PHI Detection</Heading>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Automatically scan form submissions for Protected Health Information to maintain HIPAA compliance.
              </Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0} flex="1">
                      <FormLabel color="white" mb={0}>Enable Institution-Level PHI Detection</FormLabel>
                      <Text fontSize="sm" color="gray.400">
                        Master toggle for PHI detection across all forms at this institution. Individual cohorts can have their own override settings.
                      </Text>
                    </VStack>
                    <Switch
                      isChecked={formData.enablePhiDetection || false}
                      onChange={(e) => handleUpdateField("enablePhiDetection", e.target.checked)}
                      colorScheme="red"
                    />
                  </HStack>
                </FormControl>

                {formData.enablePhiDetection && (
                  <Alert status="warning" fontSize="sm">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">PHI Detection Active</Text>
                      <Text>Form submissions will be scanned for patient names, dates of birth, social security numbers, medical record numbers, and other PHI identifiers. Flagged submissions will require instructor review before processing.</Text>
                    </VStack>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Encounter Verification */}
          <Card bg="gray.800" borderColor="gray.600">
            <CardHeader>
              <Heading size="md" color="white">Encounter Verification</Heading>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Cross-reference form submissions with patient encounter records to verify clinical documentation accuracy.
              </Text>
            </CardHeader>
            <CardBody>
              <FormControl>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0} flex="1">
                    <FormLabel color="white" mb={0}>Enable Encounter Verification</FormLabel>
                    <Text fontSize="sm" color="gray.400">
                      When enabled, clinical evaluation forms will be cross-checked against patient encounter records to verify that documented procedures and assessments match actual encounters.
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={formData.enableEncounterVerification || false}
                    onChange={(e) => handleUpdateField("enableEncounterVerification", e.target.checked)}
                    colorScheme="purple"
                  />
                </HStack>
              </FormControl>
            </CardBody>
          </Card>

          {/* Form QR Code Settings */}
          <Card bg="gray.800" borderColor="gray.600">
            <CardHeader>
              <Heading size="md" color="white">Form QR Code Settings</Heading>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Configure QR code generation for form distribution and collection workflows.
              </Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <FormControl>
                  <FormLabel color="white">QR Code Expiration Time</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.formQrCodeExpirationMinutes || 60}
                      min={5}
                      max={10080}
                      bg="gray.700"
                      borderColor="gray.600"
                      color="white"
                      w="200px"
                    >
                      <NumberInputField
                        onBlur={(e) => {
                          const newValue = parseInt(e.target.value) || 60;
                          const clamped = Math.max(5, Math.min(10080, newValue));
                          handleUpdateField("formQrCodeExpirationMinutes", clamped);
                        }}
                      />
                    </NumberInput>
                    <Text fontSize="sm" color="gray.400">minutes (5 min - 7 days)</Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    How long QR codes remain valid after generation. Shorter times are more secure; longer times are more convenient for multi-day clinical rotations.
                  </Text>
                </FormControl>

                <Divider borderColor="gray.600" />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box p={4} bg="gray.700" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Current Expiration</Text>
                    <Text fontSize="lg" fontWeight="bold" color="white">
                      {(() => {
                        const mins = formData.formQrCodeExpirationMinutes || 60;
                        if (mins < 60) return `${mins} minutes`;
                        if (mins < 1440) return `${Math.round(mins / 60 * 10) / 10} hours`;
                        return `${Math.round(mins / 1440 * 10) / 10} days`;
                      })()}
                    </Text>
                  </Box>
                  <Box p={4} bg="gray.700" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Security Level</Text>
                    <Text fontSize="lg" fontWeight="bold" color="white">
                      {(() => {
                        const mins = formData.formQrCodeExpirationMinutes || 60;
                        if (mins <= 15) return "High";
                        if (mins <= 120) return "Standard";
                        if (mins <= 1440) return "Relaxed";
                        return "Extended";
                      })()}
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        </>
      )}
    </VStack>
  );
}
